import React, { useCallback, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import MediaQuery from 'react-responsive'
import { Transition } from 'react-spring/renderprops.cjs'

import { AcornsIcon } from '@acorns/icons'
import styled from 'styled-components'

import themeValues, { ThemeProps, style } from 'theme'

import DocumentItem from './DocumentItem'

const ITEM_HEIGHT = 62
const ITEM_MARGIN_TOP = -22

type Props = {
  isMultiple: boolean
  onChange: (files: File[]) => void
  failedUploadNames?: Set<string>
}

const getBorderColor = (props: ThemeProps & { isDragAccept?: boolean }) => {
  if (props.isDragAccept) {
    return style((theme) => theme.colors.sky)
  }
  return '#eeeeee'
}

const getBackgroundColor = (props: { isDragAccept?: boolean }) => {
  if (props.isDragAccept) {
    return 'rgba(77, 161, 239, 0.05)'
  }
  return '#fff'
}

const Container = styled.div`
  margin-bottom: 20px;
`

const IconContainer = styled.div`
  margin-bottom: 16px;
`

const DropzoneContainer = styled('div')<{
  collapsed?: boolean
  isDragAccept?: boolean
}>`
  background-color: ${(props) => getBackgroundColor(props)};
  border-radius: ${style((theme) => theme.styles.borderRadius)};
  border: 2px dashed ${style((theme) => theme.colors.stoneLight)};
  height: 260px;
  margin-bottom: 20px;
  overflow: hidden;
  position: relative;
  transition:
    height 200ms ease-out,
    border-width 200ms ease-out;
  z-index: 1;
  border-color: ${(props) => getBorderColor(props)};

  ${(props) =>
    props.collapsed &&
    `
    height: 62px;
    border-width: 0;
  `};
  ${(props) =>
    !props.collapsed &&
    `
    @media screen and (max-width: ${props.theme.breakpoints.md}px) {
      height: 200px;
    }
  `};
  ${(props) =>
    !props.collapsed &&
    `
    @media screen and (max-width: ${props.theme.breakpoints.sm}px) {
      height: 287px;
    }
  `};
`

const StyledInnerDropzone = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  padding: 20px;
  width: 100%;
`

const InnerLabel = styled.div`
  color: ${style((theme) => theme.colors.slate)};
  font-size: 14px;
  font-weight: 500;
  letter-spacing: -0.4px;
`

const Underline = styled.span`
  cursor: pointer;
  text-decoration: underline;
`

const DocumentUpload = (props: Props) => {
  const [files, setFiles] = useState<File[]>([])
  const [collapsed, setCollapsed] = useState(
    !props.isMultiple && files.length > 0,
  )
  const onDrop = useCallback(
    (acceptedFiles) => {
      setCollapsed(!props.isMultiple && acceptedFiles.length > 0)
      if (!collapsed) {
        const updatedFiles = [...files, ...acceptedFiles]
        setFiles(updatedFiles)
        // acceptedFiles is unreliable/doesn't get updated by the time props.onChange is called so using variable here
        props.onChange(updatedFiles)
      }
    },
    [files],
  )
  const handleRemoveClick = (index: number) => () => {
    const changedFiles = [...files]
    changedFiles.splice(index, 1)
    setCollapsed(!props.isMultiple && changedFiles.length > 0)
    setFiles(changedFiles)
    props.onChange(changedFiles)
  }

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({ multiple: props.isMultiple, onDrop })

  return (
    <Container>
      <DropzoneContainer
        {...getRootProps({ isDragActive, isDragAccept, isDragReject })}
        collapsed={collapsed}
      >
        {collapsed ? (
          <DocumentItem
            fillHeight={true}
            height={ITEM_HEIGHT}
            index={0}
            name={files[0]?.name}
            onRemove={handleRemoveClick(0)}
          />
        ) : (
          <StyledInnerDropzone>
            {/* @ts-ignore - incompatibility between @types/react version in web-app and react-dropzone */}
            <input {...getInputProps()} aria-label="file-input" />
            <IconContainer>
              {isDragAccept ? (
                <AcornsIcon
                  icon={AcornsIcon.Icon.FileDropBlue}
                  width="63px"
                  height="85px"
                />
              ) : (
                <AcornsIcon
                  icon={AcornsIcon.Icon.FileDrop}
                  width="63px"
                  height="85px"
                />
              )}
            </IconContainer>
            <InnerLabel>
              <MediaQuery maxWidth={themeValues.breakpoints.md - 1}>
                Tap to upload a file
              </MediaQuery>
              <MediaQuery minWidth={themeValues.breakpoints.md}>
                Drop a file here to upload or{' '}
                <Underline>choose a file manually.</Underline>
              </MediaQuery>
            </InnerLabel>
          </StyledInnerDropzone>
        )}
      </DropzoneContainer>
      {props.isMultiple && (
        <Transition
          items={files.map((file) => file.name)}
          keys={files.map((_, index) => index)}
          from={{ opacity: 0, height: 0 }}
          enter={{ opacity: 1, height: ITEM_HEIGHT }}
          leave={{
            opacity: 0,
            height: 0,
            pointerEvents: 'none',
            marginTop: ITEM_MARGIN_TOP,
          }}
          native={true}
        >
          {(item, _, index) => (styles) =>
            !collapsed && (
              <DocumentItem
                key={index}
                name={item}
                index={index}
                onRemove={handleRemoveClick(index)}
                styles={styles as React.CSSProperties}
                failed={props.failedUploadNames?.has(item)}
              />
            )
          }
        </Transition>
      )}
    </Container>
  )
}

export default DocumentUpload
export { withUpload } from './upload'
export type { UploadHandlers } from './upload'
